import type { FicFidcCockpitConcentrationV1Payload } from './contract';
import type { FicFidcCockpitConcentrationStatus } from './mapFicFidcCockpitConcentration';

export type FicFidcCockpitConcentrationResultStatus =
  | FicFidcCockpitConcentrationStatus
  | 'loading'
  | 'error';

export type FicFidcCockpitConcentrationReason =
  FicFidcCockpitConcentrationV1Payload['meta']['reason'];

export interface FicFidcCockpitConcentrationFeedback {
  severity: 'error' | 'warning' | 'info';
  message: string;
}

/**
 * Feedback compartilhado pelos widgets de concentração (cards e gráfico),
 * garantindo a mesma mensagem para o mesmo estado nas duas superfícies.
 * Distingue loading, empty, partial, unavailable, zero-total,
 * missing-position e error; `ready` não produz feedback.
 */
export function getFicFidcCockpitConcentrationFeedback(
  status: FicFidcCockpitConcentrationResultStatus,
  reason: FicFidcCockpitConcentrationReason | null = null,
): FicFidcCockpitConcentrationFeedback | null {
  switch (status) {
    case 'error':
      return {
        severity: 'error',
        message: 'Não foi possível carregar a concentração da carteira.',
      };
    case 'partial':
      return {
        severity: 'warning',
        message:
          reason === 'missing-position'
            ? 'Parte das posições do FIC não foi localizada nesta competência; os valores exibidos consideram apenas as posições disponíveis.'
            : 'Os dados de concentração estão incompletos para a competência selecionada.',
      };
    case 'empty':
      return {
        severity: 'info',
        message:
          'Não há FIDCs elegíveis na carteira do FIC para a competência selecionada.',
      };
    case 'no-competence-data':
      return {
        severity: 'info',
        message:
          'Não há dados da carteira do FIC para a competência selecionada.',
      };
    case 'zero-total':
      return {
        severity: 'info',
        message:
          'O patrimônio líquido do FIC não é positivo nesta competência; a concentração não pode ser calculada.',
      };
    case 'unavailable':
      if (reason === 'missing-position') {
        return {
          severity: 'info',
          message:
            'Nenhuma posição utilizável foi localizada para calcular a concentração nesta competência.',
        };
      }

      return {
        severity: 'info',
        message:
          'A concentração não está disponível para a competência selecionada.',
      };
    default:
      return null;
  }
}
